import React, {
  useId,
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo
} from 'react';
import {
  buildFacet,
  FacetState,
  Facet as FacetType,
  FacetValue,
  NumericFacet,
  NumericFacetState,
  NumericFacetValue,
  SearchEngine,
  Tab
} from '@coveo/headless';
import RatingStars from '../../react-components/rating-stars';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import type { BodyScrollOptions } from 'body-scroll-lock';
import { createDataLayerString } from '../../helpers/helpers';
import SecondCall from './second-call-context';
import { filtersPerFacetValue, defaultTab } from '../controllers/controller';
import { addFiltersPerFacetValue } from '../../helpers/coveoFacet';
import { useTab } from './tab';

const FACET_EVENTS = Object.freeze({
  expand: 'facet:expand',
  collapse: 'facet:collapse'
});

type FacetBreadcrumbsProps = {
  title: string;
  controller: FacetType | NumericFacet;
  duplicateController?: FacetType | NumericFacet;
  certificationsLabel?: string;
  specialtiesLabel?: string;
  rangeLabels?: string[];
  rangeStarLabels?: string[];
  customOrder?: {
    facetName: string;
    iconUrl: string;
  }[];
  colors?: { value: string; color_value: string }[];
  defaultExpanded?: boolean;
  showMoreLabel?: string;
  showLessLabel?: string;
  isPrice?: boolean;
  warranties?: boolean;
  resultsMustMatch?: 'allValues' | 'atLeastOneValue';
  coveoEngine?: SearchEngine;
  field?: string;
};

function Facet({
  title,
  controller,
  duplicateController,
  certificationsLabel,
  specialtiesLabel,
  rangeLabels,
  customOrder,
  defaultExpanded,
  showMoreLabel,
  showLessLabel,
  rangeStarLabels,
  warranties,
  isPrice,
  colors,
  resultsMustMatch,
  coveoEngine,
  field
}: FacetBreadcrumbsProps) {
  const id = useId();
  const [state, setState] = useState<FacetState | NumericFacetState>(
    controller.state
  );
  const [duplicateState, setDuplicateState] = useState<
    FacetState | NumericFacetState | null
  >(duplicateController?.state || null);
  const [expanded, setExpanded] = useState<boolean>(
    defaultExpanded !== undefined ? defaultExpanded : true
  );
  const [panelHeight, setPanelHeight] = useState('100%');
  const [counter, setCounter] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const tooltipButtonRef = useRef<HTMLButtonElement>(null);
  const facetTitleRef = useRef<HTMLSpanElement>(null);
  const secondCallContext = useContext(SecondCall);

  let filterValue: string | null = null;
  let tabPerFacet: Tab | null = null;

  if (filtersPerFacetValue && filtersPerFacetValue?.length > 0) {
    const filterFacetObject = filtersPerFacetValue.find(
      facet => facet.facetField === field
    );

    if (filterFacetObject) {
      const { facetValue, context, tab, mlParameters } = filterFacetObject;
      filterValue = facetValue;

      if (mlParameters || context) {
        addFiltersPerFacetValue(
          field as string,
          facetValue,
          context,
          mlParameters
        );
      }

      if (tab) {
        tabPerFacet = useMemo(() => useTab(tab), [tab]);
      }
    }
  }

  const calculateStyles = () => {
    if (panelRef.current) {
      setPanelHeight(`${panelRef.current.scrollHeight}px`);
    }

    if (tooltipButtonRef.current && facetTitleRef.current) {
      const facetTitleWidth = facetTitleRef.current.clientWidth;

      tooltipButtonRef.current.style.setProperty(
        'inset-inline-start',
        `${facetTitleWidth + 4}px`
      );
    }
  };

  const fieldWithTooltipLabel = certificationsLabel || specialtiesLabel;

  let modal: HTMLElement | null;

  if (warranties) {
    modal = document.querySelector<HTMLElement>('.warranty-modal');
  } else {
    modal = specialtiesLabel
      ? document.querySelector<HTMLElement>('.specialty-roofing-modal')
      : document.querySelector<HTMLElement>('.certifications-modal');
  }

  const modalCloseButton = modal?.querySelector<HTMLElement>(
    '.modal__close-button'
  );

  const handleOpenModal = () => {
    const options = {
      reserveScrollBarGap: true,
      allowTouchMove: (el: HTMLElement) => {
        return el.classList.contains('modal');
      }
    } as BodyScrollOptions;
    (modal as HTMLDialogElement)?.showModal();
    disableBodyScroll(modal as HTMLElement, options);
  };

  const handleCloseModal = () => {
    (modal as HTMLDialogElement)?.close();
    clearAllBodyScrollLocks();
  };

  const expandFacet = () => {
    setExpanded(true);
  };

  const collapseFacet = () => {
    setExpanded(false);
  };

  useEffect(() => {
    const handleResize = () => {
      calculateStyles();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener(FACET_EVENTS.expand, expandFacet);
    window.addEventListener(FACET_EVENTS.collapse, collapseFacet);

    if (modalCloseButton) {
      modalCloseButton.addEventListener('click', handleCloseModal);
    }

    if (resultsMustMatch === 'allValues' && field && coveoEngine) {
      const duplicateController = buildFacet(coveoEngine, {
        options: {
          field,
          filterFacetCount: true,
          numberOfValues: 999
        }
      });

      duplicateController.subscribe(() =>
        setDuplicateState(duplicateController.state)
      );
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener(FACET_EVENTS.expand, expandFacet);
      window.removeEventListener(FACET_EVENTS.collapse, collapseFacet);

      if (modalCloseButton) {
        modalCloseButton.removeEventListener('click', handleCloseModal);
      }
    };
  }, []);

  useEffect(() => {
    calculateStyles();
  }, [state]);

  useEffect(() => {
    controller.subscribe(() => setState(controller.state));
  }, [controller]);

  const handleTriggerClick = () => {
    setExpanded(!expanded);
  };

  const handleShowMoreClick = () => {
    (controller as FacetType).showMoreValues();
    setCounter(counter + 1);
  };

  const handleShowLessClick = () => {
    for (let i = 0; i < counter; i++) {
      (controller as FacetType).showLessValues();
    }

    setCounter(0);
  };

  const secondCallFacetNumber = (
    value: string,
    numbers: { start: number; end: number }
  ) => {
    const results = secondCallContext.filteredResults.filter(result => {
      if (Array.isArray(result.raw[state.facetId])) {
        return (result.raw[state.facetId] as string[]).includes(
          value as string
        );
      } else {
        const number = result.raw[state.facetId] as number;
        return number >= numbers.start && number <= numbers.end;
      }
    });

    return results.length;
  };

  const renderList = () => {
    let values = [...state.values];

    // Filter out values with zero results
    if (!secondCallContext.secondCall) {
      values = values.filter(val => {
        const value = val as FacetValue & NumericFacetValue;
        return value.numberOfResults > 0 || value.state === 'selected';
      });
    } else {
      values = values.filter(val => {
        const value = val as FacetValue & NumericFacetValue;
        return (
          secondCallFacetNumber(value.value, value) > 0 ||
          value.state === 'selected'
        );
      });
    }

    if (customOrder) {
      values = values.sort((a, b) => {
        const aIndex = customOrder.findIndex(
          item => item.facetName === (a as FacetValue & NumericFacetValue).value
        );

        const bIndex = customOrder.findIndex(
          item => item.facetName === (b as FacetValue & NumericFacetValue).value
        );

        if (aIndex === -1) {
          return 1;
        }

        if (bIndex === -1) {
          return -1;
        }

        return aIndex - bIndex;
      });
    }

    const secondCallFilterResults = (
      value: string,
      facetValue: { start: number; end: number }
    ) => {
      if (
        secondCallContext.setFilteredResults === undefined ||
        secondCallContext.setSelectedPage === undefined ||
        secondCallContext.setSelectedFacets === undefined
      )
        return;

      if (
        secondCallContext.selectedFacets.findIndex(
          (facet: {
            facetId: string;
            value: string | { start: number; end: number };
          }) => {
            if ((facet.value as { start: number }).start) {
              return (
                (facet.value as { start: number }).start === facetValue.start &&
                facet.facetId === state.facetId
              );
            } else {
              return facet.value === value && facet.facetId === state.facetId;
            }
          }
        ) > -1
      ) {
        secondCallContext.setSelectedFacets(
          (
            facets: {
              facetId: string;
              value: string | { start: number; end: number };
            }[]
          ) =>
            facets.filter(facet =>
              facet.facetId === state.facetId && facetValue
                ? (facet.value as { start: number }).start !== facetValue.start
                : facet.value !== value
            )
        );
        secondCallContext.setSelectedPage(1);
      } else {
        secondCallContext.setSelectedFacets(facets => [
          ...facets,
          { facetId: state.facetId, value: value ? value : facetValue }
        ]);
        secondCallContext.setSelectedPage(1);
      }
    };

    if (duplicateState) {
      //filter values to only include values that are in the duplicateState
      values = values.filter(val => {
        const value = val as FacetValue & NumericFacetValue;
        return (
          duplicateState.values.findIndex(
            v => (v as FacetValue).value === value.value
          ) > -1
        );
      });
    }

    const handleCheckboxClick = (value: FacetValue & NumericFacetValue) => {
      if (filterValue) {
        const selectedValues = (controller.state.values as FacetValue[]).filter(
          (val: FacetValue) => val.state === 'selected'
        );

        const isfilterValueSelected = (selectedValues as FacetValue[]).find(
          (val: FacetValue) => val.value === filterValue
        );

        if (
          (filterValue === value.value &&
            !isfilterValueSelected &&
            selectedValues.length < 1) ||
          (filterValue !== value.value &&
            isfilterValueSelected &&
            controller.isValueSelected(value) &&
            selectedValues.length - 1 <= 1)
        ) {
          if (tabPerFacet) {
            tabPerFacet.select();
          }
        } else {
          if (tabPerFacet && defaultTab) {
            defaultTab.select();
          }
        }
      }

      if (secondCallContext.secondCall) {
        secondCallFilterResults(value.value, value);
      } else {
        controller.toggleSelect(value);
      }
    };

    return (
      <ul role="list" className={colors ? 'facet__colors' : ''}>
        {values.map((val, index) => {
          const value = val as FacetValue & NumericFacetValue;
          if (colors) {
            let colorIndex = colors.findIndex(
              item => item.value === value.value
            );
            if (colorIndex === -1) {
              colorIndex = 0;
            }

            return (
              <li key={index}>
                <div className="field field--color">
                  <input
                    data-layer={
                      value.state !== 'selected'
                        ? createDataLayerString([
                            {
                              event: 'refinement_added',
                              event_category: 'refinement usage'
                            }
                          ])
                        : undefined
                    }
                    className="field__color-input"
                    type="checkbox"
                    name={`checkbox-${id}-${index}`}
                    id={`checkbox-${id}-${index}`}
                    checked={
                      secondCallContext.secondCall
                        ? secondCallContext.selectedFacets.findIndex(facet => {
                            if ((facet.value as { start: number }).start) {
                              return (
                                (facet.value as { start: number }).start ===
                                value.start
                              );
                            } else {
                              return facet.value === value.value;
                            }
                          }) > -1
                        : value.state === 'selected'
                    }
                    onChange={() => handleCheckboxClick(value)}
                    disabled={
                      state.isLoading ||
                      (rangeStarLabels && value.numberOfResults < 1)
                    }
                  />
                  <div
                    className="field__checkbox-color"
                    style={{ background: colors[colorIndex].color_value }}
                  />

                  <div className="field__checkbox-label-wrapper">
                    <label
                      className="field__checkbox-label"
                      htmlFor={`checkbox-${id}-${index}`}
                    >
                      <span
                        className={
                          specialtiesLabel
                            ? 'field__checkbox--truncated'
                            : undefined
                        }
                      >
                        {value.value}
                      </span>
                      <span>
                        {secondCallContext.secondCall
                          ? secondCallFacetNumber(value.value, value)
                          : value.numberOfResults}
                        <span className="sr-only">results</span>
                      </span>
                    </label>
                  </div>
                </div>
              </li>
            );
          }
          return (
            <li key={index}>
              <div className="field field--checkbox ">
                <input
                  data-layer={
                    value.state !== 'selected'
                      ? createDataLayerString([
                          {
                            event: 'refinement_added',
                            event_category: 'refinement usage'
                          }
                        ])
                      : undefined
                  }
                  className="field__checkbox-input"
                  type="checkbox"
                  name={`checkbox-${id}-${index}`}
                  id={`checkbox-${id}-${index}`}
                  checked={
                    secondCallContext.secondCall
                      ? secondCallContext.selectedFacets.findIndex(facet => {
                          if ((facet.value as { start: number }).start) {
                            return (
                              (facet.value as { start: number }).start ===
                              value.start
                            );
                          } else {
                            return facet.value === value.value;
                          }
                        }) > -1
                      : value.state === 'selected'
                  }
                  onChange={() => handleCheckboxClick(value)}
                  disabled={
                    state.isLoading ||
                    (value.numberOfResults < 1 && value.state !== 'selected')
                  }
                />
                <div className="field__checkbox-label-wrapper">
                  <label
                    className="field__checkbox-label"
                    htmlFor={`checkbox-${id}-${index}`}
                  >
                    {isPrice && rangeLabels && (
                      <span>{rangeLabels[index]}</span>
                    )}
                    {rangeStarLabels && rangeLabels ? (
                      <div className="facet__stars-container">
                        <RatingStars
                          rating={value.start}
                          hideRating={true}
                          additionalClasses="facet__stars"
                        />
                        {rangeStarLabels && (
                          <span className="sr-only">
                            {rangeStarLabels[index]} {rangeLabels[index]}
                          </span>
                        )}
                        <span aria-hidden={true}>{rangeLabels[index]}</span>
                      </div>
                    ) : (
                      <span
                        className={
                          specialtiesLabel
                            ? 'field__checkbox--truncated'
                            : undefined
                        }
                      >
                        {value.value}
                        {certificationsLabel && !warranties && (
                          <img
                            className="facet__certification-icon"
                            src={
                              customOrder?.find(
                                item => item.facetName === value.value
                              )?.iconUrl
                            }
                            alt=""
                            role="presentation"
                            loading="lazy"
                            width="20"
                            height="20"
                          />
                        )}
                      </span>
                    )}
                    <span>
                      {secondCallContext.secondCall
                        ? secondCallFacetNumber(value.value, value)
                        : value.numberOfResults}
                      <span className="sr-only">results</span>
                    </span>
                  </label>
                </div>
              </div>
            </li>
          );
        })}
        <div className="facet__control-buttons">
          {!rangeStarLabels && (state as FacetState).canShowLessValues && (
            <button
              className="link--inline facet__show-less"
              onClick={handleShowLessClick}
              data-layer={createDataLayerString([
                {
                  event: 'refinement_value_accordion',
                  event_category: 'refinement usage',
                  event_action: 'show less'
                }
              ])}
              aria-label={showLessLabel}
            >
              <span>{showLessLabel}</span>
            </button>
          )}
          {!rangeStarLabels && (state as FacetState).canShowMoreValues && (
            <button
              className="link--inline facet__show-more"
              onClick={handleShowMoreClick}
              data-layer={createDataLayerString([
                {
                  event: 'refinement_value_accordion',
                  event_category: 'refinement usage',
                  event_action: 'show more'
                }
              ])}
              aria-label={showMoreLabel}
            >
              <span>{showMoreLabel}</span>
            </button>
          )}
        </div>
      </ul>
    );
  };

  return (
    <>
      {state.values.length > 0 && (
        <div className="facet">
          <button
            data-layer={
              expanded
                ? createDataLayerString([
                    {
                      event: 'accordion_interaction',
                      event_category: 'accordion usage',
                      event_action: 'expand accordion'
                    }
                  ])
                : createDataLayerString([
                    {
                      event: 'accordion_interaction',
                      event_category: 'accordion usage',
                      event_action: 'collapse accordion'
                    }
                  ])
            }
            className="facet__trigger"
            aria-expanded={expanded}
            id={`trigger-${id}`}
            aria-controls={`panel-${id}`}
            onClick={handleTriggerClick}
          >
            <span className="facet__title" ref={facetTitleRef}>
              {title}
            </span>
          </button>
          {fieldWithTooltipLabel && (
            <button
              ref={tooltipButtonRef}
              type="button"
              className="facet__tooltip btn--tooltip btn--tooltip-alt"
              title={fieldWithTooltipLabel}
              onClick={handleOpenModal}
              data-layer={createDataLayerString([
                {
                  event: 'modal_view',
                  event_category: 'modal overlay',
                  event_action: 'view',
                  event_attributes: {
                    modalName: fieldWithTooltipLabel
                      ? 'certifications'
                      : 'specialties'
                  }
                }
              ])}
            >
              <span className="sr-only">{fieldWithTooltipLabel}</span>
            </button>
          )}
          <div
            aria-hidden={!expanded}
            id={`panel-${id}`}
            role="region"
            aria-labelledby={`trigger-${id}`}
            className="facet__panel"
            ref={panelRef}
            style={{ maxBlockSize: expanded ? panelHeight : 0 }}
          >
            {state.values.length > 0 && renderList()}
          </div>
        </div>
      )}
    </>
  );
}

export default Facet;
